import type { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';
import { ChevronLeft } from 'lucide-react';
import { cn } from './utils';

/**
 * Page-grammar primitive (SIL-538, W0 of
 * resources/plans/wayfinder-ux-consistency-spec.md §1.1-1.2): chevron +
 * label, currently hand-rolled per route (usually with `ArrowLeft` from
 * lucide and one-off zinc classes). Polymorphic via `as` — this package
 * carries no router dependency, so a call site passes its own router
 * `Link` (`as={Link} to="/assets"`) or leaves the default `<a href="…">`.
 */
export function BackLink<E extends ElementType = 'a'>({
  as,
  children,
  className,
  ...rest
}: {
  as?: E;
  children: ReactNode;
  className?: string;
} & Omit<ComponentPropsWithoutRef<E>, 'as' | 'children' | 'className'>) {
  const Component = (as ?? 'a') as ElementType;
  return (
    <Component
      className={cn(
        'inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground',
        className,
      )}
      {...rest}
    >
      <ChevronLeft className="size-4" />
      {children}
    </Component>
  );
}
